import { IS_PROD_ENV, typedEnv } from '@app/environment/typed-env';
import { DesignOutDto, QRCodeOutDto, WiFiQRCodeOutDto } from '@app/swagger-types';
import { WithNullableKeys } from '@app/utils/type.utils';
import { DesignFormSchema } from '../template/api/template.form.schema.api';
import { QRCodeFormSchema } from './api/qr-code.form.schema.api';
import { QRRendererProps } from './components/QRCodeRendererSVG';

export const getDeviceId = () => {
  return localStorage.getItem('deviceId');
};

export const setDeviceId = (id: string) => {
  localStorage.setItem('deviceId', id);
};

export const getLinkForCode = (id: string) => {
  return `${typedEnv.REACT_APP_SCAN_HOST_URL}/${id}`;
};

export const padGtin = (gtin: string): string => {
  return gtin.padStart(14, '0');
};

export const getLinkForGS1 = (id: string, gtin: string) => {
  const formattedGtin = padGtin(gtin);
  return `${typedEnv.REACT_APP_SCAN_HOST_URL}/${id}/01/${formattedGtin}`;
};

export const getLinkForQrCodeTemplate = (id: string) => {
  return `${typedEnv.REACT_APP_HOST_URL}/templates/${id}/edit`;
};

export const getQRScanLink = (qrId: string, gtin?: string) => {
  if (qrId && gtin) {
    return getLinkForGS1(qrId, gtin);
  }
  if (qrId) {
    return getLinkForCode(qrId);
  }
  return typedEnv.REACT_APP_HOST_URL;
};

export const replaceSubdomain = (
  newSubdomain: string,
  stripProtocol = false,
  baseUrl = typedEnv.REACT_APP_SCAN_HOST_URL
) => {
  try {
    const url = new URL(baseUrl);
    const hostParts = url.hostname.split('.');

    if (newSubdomain) {
      if (hostParts.length > 2) {
        hostParts[0] = newSubdomain;
      } else {
        hostParts.unshift(newSubdomain);
      }
    } else if (hostParts.length > 2) {
      hostParts.shift();
    }

    url.hostname = hostParts.join('.');

    return stripProtocol ? url.hostname + url.pathname + url.search + url.hash : url.toString();
  } catch (error) {
    console.error('Invalid URL:', baseUrl);
    return baseUrl;
  }
};

export const getWiFiQRCode = (data: Pick<WiFiQRCodeOutDto, 'hidden' | 'encryption' | 'password' | 'ssid'>): string => {
  const { ssid, encryption, password, hidden } = data;
  if (!ssid || !encryption) {
    throw new Error('SSID and encryption type are required parameters.');
  }
  const encryptionType = encryption.toUpperCase() === 'NONE' ? 'nopass' : encryption;
  const params = [`S:${ssid}`, `T:${encryptionType}`];
  if (password) {
    params.push(`P:${password}`);
  }
  params.push(`H:${hidden}`);
  return `WIFI:${params.join(';')};;`;
};

const ENV_SUFFIX = IS_PROD_ENV ? '' : typedEnv.REACT_APP_ENV_NAME;
export const getQRfileNameForDownload = (qrId?: string, fileName = '') => {
  if (qrId && !fileName.includes(qrId)) {
    if (!fileName) {
      fileName = 'qr-code.png';
    }
    const delimiter = /\s/.test(fileName) ? ' ' : '-';
    const maybeDelimiter = IS_PROD_ENV ? '' : delimiter;
    const name = fileName.replace(/\.png$/, '');
    const ext = 'png';
    fileName = `${name}${delimiter}${qrId}${maybeDelimiter}${ENV_SUFFIX}.${ext}`;
  }
  if (!fileName) {
    const delimiter = IS_PROD_ENV ? '' : '-';
    fileName = `qr-code${delimiter}${ENV_SUFFIX}.png`;
  }
  return fileName;
};

export const extractStylePropsFromQRForm = (form: QRCodeFormSchema, template?: DesignOutDto) => {
  const { patternColor, cornerColor, patternType, cornerType, libraryId, templateId } = form;
  const qrProps =
    templateId && template
      ? {
          backgroundColor: template.backgroundColor,
        }
      : {
          backgroundColor: form.backgroundColor,
        };
  const frameProps: Partial<DesignOutDto> =
    templateId && template
      ? {
          frameType: template.frameType,
          frameText: template.frameText,
          frameTextSize: template.frameTextSize,
          frameTextColor: template.frameTextColor,
          frameBackgroundColor: template.frameBackgroundColor,
        }
      : {
          frameType: form.frameType,
          frameText: form.frameText || undefined,
          frameTextSize: form.frameTextSize,
          frameTextColor: form.frameTextColor,
          frameBackgroundColor: form.frameBackgroundColor,
        };
  return { ...qrProps, patternColor, cornerColor, patternType, cornerType, libraryId, ...frameProps };
};

export const extractStylePropsFromQRTemplateForm = (form: DesignFormSchema): Partial<DesignFormSchema> => {
  const {
    backgroundColor,
    patternColor,
    cornerColor,
    patternType,
    cornerType,
    libraryId,
    frameType,
    frameText,
    frameTextSize,
    frameTextColor,
    frameBackgroundColor,
  } = form;
  return {
    backgroundColor,
    patternColor,
    cornerColor,
    patternType,
    cornerType,
    libraryId,
    frameType,
    frameText,
    frameTextSize,
    frameTextColor,
    frameBackgroundColor,
  };
};

export const extractStylePropsFromQRDesign = (
  design?: WithNullableKeys<DesignOutDto, 'createdBy' | 'template' | 'library'> | null
) => {
  if (!design) {
    return {};
  }
  const {
    backgroundColor,
    patternColor,
    cornerColor,
    patternType,
    cornerType,
    frameType,
    frameText,
    frameTextSize,
    frameTextColor,
    frameBackgroundColor,
  } = design;
  const libraryId = design.library?.id;
  return {
    backgroundColor,
    patternColor,
    cornerColor,
    patternType,
    cornerType,
    libraryId,
    frameType,
    frameText,
    frameTextSize,
    frameTextColor,
    frameBackgroundColor,
  };
};

// TODO match QRCodeOutDto with schemas to avoid Omit and WithNullableKeys
export const extractRenderPropsFromQR = (
  qr?:
    | (Omit<QRCodeOutDto, 'website' | 'design' | 'createdWithFlow'> & {
        design?: WithNullableKeys<Omit<DesignOutDto, 'template'>, 'library'> | null;
      })
    | null
): Partial<QRRendererProps> => {
  const design = qr?.design;
  const { publicLogoType } = qr || {};
  if (!design) {
    return {
      publicLogoType,
    };
  }
  const {
    backgroundColor,
    patternColor,
    cornerColor,
    patternType,
    cornerType,
    frameType,
    frameText,
    frameTextSize,
    frameTextColor,
    frameBackgroundColor,
  } = design;
  const libraryId = design.library?.id;
  return {
    // from design
    backgroundColor,
    patternColor,
    cornerColor,
    patternType,
    cornerType,
    libraryId,
    frameType,
    frameText,
    frameTextSize,
    frameTextColor,
    frameBackgroundColor,
    // from root
    publicLogoType,
  };
};
